import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { TInsurance } from "../../hooks/api/useGetInsuranceById";

interface CustomerInsuranceState {
  insurance: TInsurance | null;
  isLoading: boolean;
  error: string | null;
  setInsurance: (insurance: TInsurance) => void;
  updateInsurance: (insurance: Partial<TInsurance>) => void;
  updateInsuranceAsync: (insurance: Partial<TInsurance>) => Promise<void>;
  clearInsurance: () => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  insurance: null,
  isLoading: false,
  error: null,
};

export const useCustomerInsuranceStore = create<CustomerInsuranceState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setInsurance: (insurance: TInsurance) =>
          set({
            insurance,
            isLoading: false,
            error: null,
          }),
        updateInsurance: (insurance: Partial<TInsurance>) =>
          set((state) => ({
            insurance: state.insurance
              ? { ...state.insurance, ...insurance }
              : (insurance as TInsurance),
          })),
        updateInsuranceAsync: async (insurance: Partial<TInsurance>) => {
          set({ isLoading: true, error: null });
          try {
            await new Promise((resolve) => setTimeout(resolve, 0));
            set((state) => ({
              insurance: state.insurance
                ? { ...state.insurance, ...insurance }
                : (insurance as TInsurance),
              isLoading: false,
              error: null,
            }));
          } catch (err) {
            set({
              error:
                err instanceof Error
                  ? err.message
                  : "Failed to update insurance",
              isLoading: false,
            });
          }
        },
        clearInsurance: () =>
          set({
            insurance: null,
            error: null,
          }),
        setIsLoading: (isLoading: boolean) =>
          set({
            isLoading,
          }),
        setError: (error: string | null) =>
          set({
            error,
            isLoading: false,
          }),
        reset: () => set(initialState),
      }),
      {
        name: "customer-insurance-store",
      },
    ),
  ),
);
